import { authConstants, cartConstants, desiresConstants } from "./constants";
import axios from "../helpers/axios";
import swal from 'sweetalert';
import { setShowModalUser } from "../reducers/auth.reducer";
import { reconnectSocketUser } from "../socket/socket";

const emptySubscription = {
  type: "none",
  plan: "none",
  validUntil: null,
  autoRenew: false,
  status: "inactive",
  canceledAt: null,
  lastRenewalAt: null,
  nextBillingAt: null
};

const normalizeUser = (user = {}) => {
  return {
    ...user,
    balance: Number(user.balance || 0),
    subscription: {
      ...emptySubscription,
      ...(user.subscription || {})
    }
  };
};

const getEmailVerified = (user = {}) => {
  return !!(
    user.emailVerified ||
    user.isEmailVerified ||
    user.emailConfirmed ||
    user.verifiedEmail
  );
};

const saveAuth = (token, user) => {
  const previousToken = localStorage.getItem("token") || "";

  if (token) {
    localStorage.setItem("token", token);
  }

  if (user) {
    localStorage.setItem("user", JSON.stringify(normalizeUser(user)));
  }

  if (token && String(token) !== String(previousToken)) {
    reconnectSocketUser();
  }
};

const saveUser = (user) => {
  if (user) {
    localStorage.setItem("user", JSON.stringify(normalizeUser(user)));
  }
};

const getStoredUser = () => {
  try {
    const user = localStorage.getItem("user");
    return user ? normalizeUser(JSON.parse(user)) : null;
  } catch (e) {
    return null;
  }
};

const isTechnicalMailError = (message) => {
  const text = String(message || "").toLowerCase();

  return (
    text.includes("invalid login") ||
    text.includes("smtp") ||
    text.includes("esocket") ||
    text.includes("eauth") ||
    text.includes("wrong version number") ||
    text.includes("bad credentials")
  );
};

const getErrorMessage = (error, fallback = "Что-то пошло не так") => {
  const message = (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );

  if (isTechnicalMailError(message)) {
    return fallback;
  }

  return message;
};

export const signup = (userPayload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: authConstants.SIGNUP_REQUEST });

      const res = await axios.post(`/signup`, userPayload);

      if (res.status === 201) {
        const { token, user, emailVerificationSent, message } = res.data;
        const normalizedUser = normalizeUser(user);
        const emailVerified = getEmailVerified(normalizedUser);

        saveAuth(token, normalizedUser);

        dispatch({
          type: authConstants.SIGNUP_SUCCESS,
          payload: {
            token,
            user: normalizedUser,
            emailVerificationSent: !!emailVerificationSent,
            message,
          }
        });

        dispatch({
          type: authConstants.LOGIN_SUCCESS,
          payload: {
            token,
            user: normalizedUser,
            keepModalOpen: !emailVerified,
            modalMode: "signup",
          },
        });

        return {
          ok: true,
          token,
          user: normalizedUser,
          emailVerificationSent: !!emailVerificationSent,
          message: message || "Регистрация прошла успешно"
        };
      }

      dispatch({
        type: authConstants.SIGNUP_FAILURE,
        payload: {
          message: res.data.message
        }
      });

      return {
        ok: false,
        message: res.data.message || "Ошибка регистрации"
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      dispatch({
        type: authConstants.SIGNUP_FAILURE,
        payload: {
          message: errorMessage
        }
      });

      return {
        ok: false,
        message: errorMessage
      };
    }
  };
};

export const login = (userPayload) => {
  return async (dispatch) => {
    dispatch({ type: authConstants.LOGIN_REQUEST });

    try {
      const res = await axios.post("/signin", { ...userPayload });

      if (res.status === 200) {
        const { token, user } = res.data;
        const normalizedUser = normalizeUser(user);
        const emailVerified = getEmailVerified(normalizedUser);

        saveAuth(token, normalizedUser);

        dispatch({
          type: authConstants.LOGIN_SUCCESS,
          payload: {
            token,
            user: normalizedUser,
            keepModalOpen: !emailVerified,
            modalMode: "signup",
          },
        });

        if (emailVerified) {
          dispatch(setShowModalUser({ forceShow: false }));

          swal({
            title: `Приветствуем на нашем сайте ${normalizedUser.firstName} ${normalizedUser.lastName}`,
            text: "Что вас интересует???",
            icon: "success",
          });
        }

        return true;
      }

      dispatch({
        type: authConstants.LOGIN_FAILURE,
        payload: {
          error: "Неправильный логин или пароль"
        }
      });

      return false;
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Неправильный логин или пароль");

      dispatch({
        type: authConstants.LOGIN_FAILURE,
        payload: {
          error: errorMessage
        }
      });

      return false;
    }
  };
};

export const sendEmailVerification = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: authConstants.EMAIL_VERIFICATION_SEND_REQUEST });

      const res = await axios.post("/user/email-verification/send");

      dispatch({
        type: authConstants.EMAIL_VERIFICATION_SEND_SUCCESS,
        payload: res.data
      });

      return {
        ok: true,
        message: res.data?.message || "Код подтверждения отправлен на email",
        secondsLeft: res.data?.secondsLeft || 0
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Ошибка отправки кода подтверждения");

      dispatch({
        type: authConstants.EMAIL_VERIFICATION_SEND_FAILURE,
        payload: {
          message: errorMessage,
          secondsLeft: error?.response?.data?.secondsLeft || 0
        }
      });

      return {
        ok: false,
        message: errorMessage,
        secondsLeft: error?.response?.data?.secondsLeft || 0
      };
    }
  };
};

export const confirmEmailVerification = ({ code }) => {
  return async (dispatch) => {
    try {
      const cleanCode = String(code || "").trim();

      if (!cleanCode) {
        return {
          ok: false,
          message: "Введите код подтверждения"
        };
      }

      dispatch({ type: authConstants.EMAIL_VERIFICATION_CONFIRM_REQUEST });

      const res = await axios.post("/user/email-verification/confirm", {
        code: cleanCode
      });

      const normalizedUser = normalizeUser(res.data?.user || {});
      saveUser(normalizedUser);

      dispatch({
        type: authConstants.EMAIL_VERIFICATION_CONFIRM_SUCCESS,
        payload: {
          ...res.data,
          user: normalizedUser
        }
      });

      return {
        ok: true,
        message: res.data?.message || "Email успешно подтверждён",
        user: normalizedUser
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Ошибка подтверждения email");

      dispatch({
        type: authConstants.EMAIL_VERIFICATION_CONFIRM_FAILURE,
        payload: {
          message: errorMessage,
          attemptsLeft: error?.response?.data?.attemptsLeft
        }
      });

      return {
        ok: false,
        message: errorMessage,
        attemptsLeft: error?.response?.data?.attemptsLeft
      };
    }
  };
};

export const requestPasswordReset = ({ email }) => {
  return async (dispatch) => {
    try {
      const cleanEmail = String(email || "").trim().toLowerCase();

      if (!cleanEmail) {
        return {
          ok: false,
          message: "Введите email",
        };
      }

      dispatch({ type: authConstants.PASSWORD_RESET_REQUEST_REQUEST });

      const res = await axios.post("/user/password-reset/request", {
        email: cleanEmail,
      });

      dispatch({
        type: authConstants.PASSWORD_RESET_REQUEST_SUCCESS,
        payload: res.data
      });

      return {
        ok: true,
        message: res.data?.message || "Код восстановления отправлен на email",
        secondsLeft: res.data?.secondsLeft || 0,
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Ошибка отправки кода восстановления");

      dispatch({
        type: authConstants.PASSWORD_RESET_REQUEST_FAILURE,
        payload: {
          message: errorMessage,
          secondsLeft: error?.response?.data?.secondsLeft || 0
        }
      });

      return {
        ok: false,
        message: errorMessage,
        secondsLeft: error?.response?.data?.secondsLeft || 0,
      };
    }
  };
};

export const confirmPasswordReset = ({ email, code, newPassword, confirmPassword }) => {
  return async (dispatch) => {
    try {
      const cleanEmail = String(email || "").trim().toLowerCase();
      const cleanCode = String(code || "").trim();

      if (!cleanEmail) {
        return {
          ok: false,
          message: "Введите email",
        };
      }

      if (!cleanCode) {
        return {
          ok: false,
          message: "Введите код из письма",
        };
      }

      if (!newPassword || String(newPassword).length < 6) {
        return {
          ok: false,
          message: "Новый пароль должен быть не короче 6 символов",
        };
      }

      if (newPassword !== confirmPassword) {
        return {
          ok: false,
          message: "Пароли не совпадают",
        };
      }

      dispatch({ type: authConstants.PASSWORD_RESET_CONFIRM_REQUEST });

      const res = await axios.post("/user/password-reset/confirm", {
        email: cleanEmail,
        code: cleanCode,
        newPassword,
        confirmPassword,
      });

      dispatch({
        type: authConstants.PASSWORD_RESET_CONFIRM_SUCCESS,
        payload: res.data
      });

      return {
        ok: true,
        message: res.data?.message || "Пароль успешно изменён",
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Ошибка восстановления пароля");

      dispatch({
        type: authConstants.PASSWORD_RESET_CONFIRM_FAILURE,
        payload: {
          message: errorMessage,
          attemptsLeft: error?.response?.data?.attemptsLeft
        }
      });

      return {
        ok: false,
        message: errorMessage,
        attemptsLeft: error?.response?.data?.attemptsLeft,
      };
    }
  };
};

export const refreshCurrentUser = () => {
  return async (dispatch) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return false;
      }

      const res = await axios.get("/auth/me");

      if (res.status === 200 && res.data.user) {
        const normalizedUser = normalizeUser(res.data.user);

        saveAuth(token, normalizedUser);

        dispatch({
          type: authConstants.LOGIN_SUCCESS,
          payload: {
            token,
            user: normalizedUser,
            keepModalOpen: !getEmailVerified(normalizedUser),
            modalMode: "signup",
          },
        });

        return true;
      }

      return false;
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;

      if (status === 401 && message === "jwt expired") {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        reconnectSocketUser();

        dispatch({
          type: authConstants.LOGOUT_BY_SESSION_EXPIRED,
          payload: {
            message: "Сессия истекла"
          }
        });

        return false;
      }

      return false;
    }
  };
};

export const isUserLoggedIn = () => {
  return async (dispatch) => {
    const token = localStorage.getItem("token");
    const user = getStoredUser();

    if (!token || !user) {
      dispatch({
        type: authConstants.LOGIN_FAILURE,
        payload: {
          error: "Failed to login",
          message: "Что-то пошло не так, попробуйте снова!"
        },
      });

      return false;
    }

    dispatch({
      type: authConstants.LOGIN_SUCCESS,
      payload: {
        token,
        user,
        keepModalOpen: !getEmailVerified(user),
        modalMode: "signup",
      },
    });

    reconnectSocketUser();
    dispatch(refreshCurrentUser());

    return true;
  };
};

export const signout = () => {
  return async (dispatch) => {
    dispatch({ type: authConstants.LOGOUT_REQUEST });

    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('galleryItemData');
      localStorage.removeItem('cart');

      const res = await axios.post(`/admin/signout`);

      localStorage.clear();
      reconnectSocketUser();

      dispatch({
        type: authConstants.LOGOUT_SUCCESS,
        payload: {
          message: res.data.message
        }
      });

      dispatch({ type: desiresConstants.RESET_DESIRES });
      dispatch({ type: cartConstants.RESET_CART });

      return true;
    } catch (error) {
      localStorage.clear();
      reconnectSocketUser();

      dispatch({
        type: authConstants.LOGOUT_SUCCESS,
        payload: {
          message: "Вы вышли из аккаунта"
        }
      });

      dispatch({ type: desiresConstants.RESET_DESIRES });
      dispatch({ type: cartConstants.RESET_CART });

      return true;
    }
  };
};
